import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'

const clientRowSchema = z.object({
  mac: z.string(),
  ip: z.string().nullable(),
  vendor: z.string(),
  vlan: z.string().nullable(),
  hostname: z.string().nullable(),
  seenIn: z.array(z.string()),
  lastSeenAt: z.string().nullable(),
})

// GET /v1/locations/:code/clients -> { code, success, data: { clients, lastRefreshedAt, isStale } }
const clientsEnvelopeSchema = z.object({
  success: z.boolean(),
  data: z.object({
    clients: z.array(clientRowSchema),
    lastRefreshedAt: z.string().nullable(),
    isStale: z.boolean(),
  }),
})

export type ClientRow = z.infer<typeof clientRowSchema>
export type LocationClients = z.infer<typeof clientsEnvelopeSchema>['data']

export const clientsQueryKey = (idOrCode: string, noIpOnly: boolean) => ['clients', idOrCode, noIpOnly] as const

/** Cached client snapshot for the store. `noIpOnly` filters to clients with no IP (server-side). */
export function useLocationClients(idOrCode: string, opts: { noIpOnly?: boolean } = {}) {
  const api = useApi()
  const preview = usePreviewData()
  const noIpOnly = opts.noIpOnly ?? false
  return useQuery({
    queryKey: preview ? ['preview', 'clients', noIpOnly] : clientsQueryKey(idOrCode, noIpOnly),
    queryFn: async (): Promise<LocationClients> => {
      const res = await api.get(
        `/v1/locations/${encodeURIComponent(idOrCode)}/clients${noIpOnly ? '?noIpOnly=true' : ''}`,
        clientsEnvelopeSchema,
      )
      return res.data
    },
    enabled: !preview && idOrCode.length > 0,
    ...(preview
      ? {
          initialData: noIpOnly
            ? { ...preview.clients, clients: preview.clients.clients.filter((c) => c.ip === null) }
            : preview.clients,
          staleTime: Number.POSITIVE_INFINITY,
        }
      : {}),
  })
}

export type RefreshStatus = 'queued' | 'running' | 'paused' | 'errored' | 'terminated' | 'complete'

/** Pure: workflow statuses after which polling stops. */
export const isTerminalRefreshStatus = (s: RefreshStatus): boolean =>
  s === 'complete' || s === 'errored' || s === 'terminated'

const refreshStartSchema = z.object({
  success: z.boolean(),
  data: z.array(z.object({ aptosCode: z.string(), workflowInstanceId: z.string() })),
})
const refreshStatusSchema = z.object({
  success: z.boolean(),
  data: z.object({
    status: z.enum(['queued', 'running', 'paused', 'errored', 'terminated', 'complete']),
    error: z.string().optional(),
  }),
})

export interface RefreshClientsResult {
  status: RefreshStatus
  error?: string
}

/**
 * Refresh the client snapshot: POST /v1/clients/refresh `{aptosCodes}`, then poll
 * GET /v1/clients/refresh/:instanceId until terminal (replaces v1's fetcher loop). Resolves the final status
 * (`complete`/`errored`/`terminated`); the clients query is invalidated afterwards. Polls every `intervalMs`
 * (default 2000) up to `maxPolls` (default 60); a timeout resolves `{status:'running'}`.
 * `mutate({ aptosCodes, intervalMs?, maxPolls? })`.
 */
export function useRefreshClients() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: {
      aptosCodes: string[]
      intervalMs?: number
      maxPolls?: number
    }): Promise<RefreshClientsResult> => {
      if (preview) return { status: 'complete' }
      const start = await api.request(
        '/v1/clients/refresh',
        { method: 'POST', body: { aptosCodes: v.aptosCodes } },
        refreshStartSchema,
      )
      const instanceId = start.data[0]?.workflowInstanceId
      if (!instanceId) throw new Error('Refresh did not return a workflow instance')
      const interval = v.intervalMs ?? 2000
      for (let i = 0; i < (v.maxPolls ?? 60); i++) {
        await new Promise((r) => setTimeout(r, interval))
        const res = await api.get(`/v1/clients/refresh/${encodeURIComponent(instanceId)}`, refreshStatusSchema)
        if (isTerminalRefreshStatus(res.data.status)) {
          return { status: res.data.status, ...(res.data.error ? { error: res.data.error } : {}) }
        }
      }
      return { status: 'running' }
    },
    onSettled: async () => {
      if (!preview) await qc.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}
